import { useEffect, useState, type ReactNode } from 'react'
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router'

import { api, forgetCsrfToken, setSessionLostHandler, type Business } from './api.js'
import { CardIcon, DownloadIcon, GridIcon, MonitorIcon, SettingsIcon, SignOutIcon } from './components/icons.js'
import { initials } from './components/ui.js'
import { Wordmark } from './components/wordmark.js'
import { Billing } from './screens/Billing.js'
import { Devices } from './screens/Devices.js'
import { Download } from './screens/Download.js'
import { Overview } from './screens/Overview.js'
import { PaymentReturn } from './screens/PaymentReturn.js'
import { Register } from './screens/Register.js'
import { Settings } from './screens/Settings.js'
import { SignIn } from './screens/SignIn.js'

type AuthState = 'checking' | 'signed-in' | 'signed-out'

type PageMeta = { title: string; subtitle: string; icon: (p: { className?: string }) => ReactNode }

/** One place defining a page's name, blurb and icon, so they cannot drift. */
const PAGE_META: Record<string, PageMeta> = {
  '/': {
    title: 'Overview',
    subtitle: 'Your subscription, your till software, and the device running it',
    icon: GridIcon,
  },
  '/billing': {
    title: 'Billing',
    subtitle: 'Pay for the month and see what you have paid',
    icon: CardIcon,
  },
  '/download': {
    title: 'Download',
    subtitle: 'The till software, and how to check the file you downloaded',
    icon: DownloadIcon,
  },
  '/devices': {
    title: 'Devices',
    subtitle: 'The machine your till runs on, its backups, and how to replace it',
    icon: MonitorIcon,
  },
  '/settings': {
    title: 'Settings',
    subtitle: 'Your business details and how they appear on a receipt',
    icon: SettingsIcon,
  },
}

function Rail({ business, onSignOut }: { business: Business | null; onSignOut: () => void }) {
  const [signingOut, setSigningOut] = useState(false)

  return (
    <aside className="rail">
      <div className="rail__brand">
        <Wordmark className="wordmark--sm" />
        <span className="rail__brand-text">
          <span className="rail__brand-name">{business?.name ?? 'Your business'}</span>
          <span className="rail__brand-sub">Business account</span>
        </span>
      </div>

      <nav className="rail__nav">
        {Object.entries(PAGE_META).map(([to, meta]) => {
          const Icon = meta.icon
          return (
            <NavLink key={to} to={to} end={to === '/'} className="rail__link">
              <Icon />
              <span className="rail__label">{meta.title}</span>
            </NavLink>
          )
        })}
      </nav>

      <div className="rail__user">
        <span className="rail__avatar" aria-hidden="true">
          {initials(business?.email ?? business?.name ?? '?')}
        </span>
        <span className="rail__user-text">
          <span className="rail__user-name">{business?.name ?? 'Signed in'}</span>
          <span className="rail__user-mail">{business?.email ?? ''}</span>
        </span>
        <button
          type="button"
          className="rail__icon-btn"
          title="Sign out"
          aria-label="Sign out"
          disabled={signingOut}
          onClick={() => {
            setSigningOut(true)
            onSignOut()
          }}
        >
          <SignOutIcon />
        </button>
      </div>
    </aside>
  )
}

function Topline() {
  const { pathname } = useLocation()
  const meta = PAGE_META[pathname] ?? PAGE_META['/']!
  const Icon = meta.icon

  return (
    <div className="topline">
      <span className="topline__mark">
        <Icon />
      </span>
      <div>
        <h2>{meta.title}</h2>
        <p>{meta.subtitle}</p>
      </div>
    </div>
  )
}

function Workspace({ onSignedOut }: { onSignedOut: () => void }) {
  const navigate = useNavigate()
  const [business, setBusiness] = useState<Business | null>(null)

  useEffect(() => {
    void api
      .business()
      .then(setBusiness)
      .catch(() => undefined)
  }, [])

  async function signOut() {
    await api.signOut().catch(() => undefined)
    forgetCsrfToken()
    onSignedOut()
    void navigate('/sign-in', { replace: true })
  }

  return (
    <div className="app">
      <Rail business={business} onSignOut={() => void signOut()} />
      <div className="content">
        <Topline />
        <main>
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/download" element={<Download />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/billing/return" element={<PaymentReturn />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

export function App() {
  const [auth, setAuth] = useState<AuthState>('checking')

  useEffect(() => {
    let cancelled = false
    api
      .session()
      .then(() => {
        if (!cancelled) setAuth('signed-in')
      })
      .catch(() => {
        if (!cancelled) setAuth('signed-out')
      })
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * A session can expire while the portal is open — an owner leaves the tab up
   * overnight and comes back to it. Without this, the next action returns 401
   * and whichever screen made it renders "Something went wrong", which is both
   * inaccurate and unactionable: the thing to do is sign in again. Registered
   * once, at the shell, because it is the only thing that can flip the routes.
   */
  useEffect(() => {
    setSessionLostHandler(() => setAuth('signed-out'))
    return () => setSessionLostHandler(null)
  }, [])

  // 'checking' renders nothing rather than redirecting: bouncing a signed-in
  // owner to the sign-in page for a moment on every load looks like being
  // logged out, and they will sign in again unnecessarily.
  if (auth === 'checking') return null

  return (
    <BrowserRouter>
      {auth === 'signed-out' ? (
        <Routes>
          <Route path="/register" element={<Register onSignedIn={() => setAuth('signed-in')} />} />
          <Route path="/sign-in" element={<SignIn onSignedIn={() => setAuth('signed-in')} />} />
          <Route path="*" element={<Navigate to="/sign-in" replace />} />
        </Routes>
      ) : (
        <Workspace onSignedOut={() => setAuth('signed-out')} />
      )}
    </BrowserRouter>
  )
}
